import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { HelpCircle, X, Sparkles, Loader2, BookOpen, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { HELP_DEFAULTS, HELP_LABELS } from "@/lib/helpContent";
import { activeF1Contexts } from "@/lib/useF1Help";

// Verweise im Hilfetext: [[→ Aufgaben öffnen|/module/aufgaben]] wird ein Knopf, der DIREKT an die Stelle führt.
// Einstellungen: /settings?tab=kategorien öffnet den Reiter. Wer im eigenen Kalender-Fenster (/kalender) liest,
// bekommt die Stelle in einem neuen Fenster, damit die offene Eingabe nicht verloren geht.
const VERWEIS = /\[\[([^\]|]+)\|([^\]]+)\]\]/g;
export const teileMitVerweisen = (text) => {
  const teile = [];
  let letzte = 0;
  const s = String(text || "");
  for (const m of s.matchAll(VERWEIS)) {
    if (m.index > letzte) teile.push({ text: s.slice(letzte, m.index) });
    teile.push({ label: m[1].trim(), ziel: m[2].trim() });
    letzte = m.index + m[0].length;
  }
  if (letzte < s.length) teile.push({ text: s.slice(letzte) });
  return teile;
};

/**
 * Globales Hilfe-Slide-Over rechts.
 *
 * Hoert auf das CustomEvent "graupner:f1-help" (siehe useF1Help).
 * Laedt Hilfetexte aus module_textvorlagen mit doc_type=<context> und text_type=hilfe.
 * Faellt auf HELP_DEFAULTS zurueck, wenn keine Vorlagen vorhanden sind.
 *
 * In App.js einmal global gerendert.
 */
export const HelpSlideOver = () => {
  const [open, setOpen] = useState(false);
  const [context, setContext] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [usingDefaults, setUsingDefaults] = useState(false);
  const navigate = useNavigate();

  const close = useCallback(() => { setOpen(false); }, []);
  const zumZiel = (ziel) => {
    setOpen(false);
    if (window.location.pathname.startsWith("/kalender")) window.open(ziel, "_blank");
    else navigate(ziel);
  };

  // F1-Event-Listener
  useEffect(() => {
    const onF1 = (e) => {
      const ctx = e?.detail?.context;
      if (!ctx) return;
      setContext(ctx);
      setOpen(true);
    };
    window.addEventListener("graupner:f1-help", onF1);
    return () => window.removeEventListener("graupner:f1-help", onF1);
  }, []);

  // Globaler F1-Fallback: Workflow-Uebersicht oeffnen, wenn die aktuelle Seite
  // KEINEN eigenen F1-Hilfe-Kontext registriert hat (Regel 13: nutzt das
  // bestehende HelpSlideOver statt eines neuen Modals).
  useEffect(() => {
    const onGlobalF1 = (e) => {
      if (e.key !== "F1") return;
      if (activeF1Contexts.size > 0) return; // Seite behandelt F1 selbst
      e.preventDefault();
      setContext("hilfe_workflows");
      setOpen(true);
    };
    window.addEventListener("keydown", onGlobalF1);
    return () => window.removeEventListener("keydown", onGlobalF1);
  }, []);

  // ESC schliesst
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  // Laden, sobald geoeffnet + context gesetzt
  const load = useCallback(async () => {
    if (!context) return;
    setLoading(true);
    try {
      const r = await api.get("/modules/textvorlagen/data", {
        params: { doc_type: context, text_type: "hilfe" },
      });
      const data = Array.isArray(r.data) ? r.data : [];
      if (data.length > 0) {
        data.sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999) || (a.title || "").localeCompare(b.title || ""));
        // Neue Standard-Themen (z. B. nach einem Update) zeigen, auch wenn eigene Texte gespeichert sind
        const titel = new Set(data.map((d) => (d.title || "").trim()));
        setItems([...data, ...(HELP_DEFAULTS[context] || []).filter((d) => !titel.has(d.title.trim()))]);
        setUsingDefaults(false);
      } else {
        setItems(HELP_DEFAULTS[context] || []);
        setUsingDefaults(true);
      }
    } catch {
      setItems(HELP_DEFAULTS[context] || []);
      setUsingDefaults(true);
    } finally {
      setLoading(false);
    }
  }, [context]);

  useEffect(() => { if (open && context) load(); }, [open, context, load]);

  const seedDefaults = async () => {
    if (!context || seeding) return;
    const defaults = HELP_DEFAULTS[context] || [];
    if (defaults.length === 0) { toast.error("Keine Default-Texte vorhanden"); return; }
    setSeeding(true);
    try {
      for (let i = 0; i < defaults.length; i++) {
        const d = defaults[i];
        await api.post("/modules/textvorlagen/data", {
          doc_type: context,
          text_type: "hilfe",
          title: d.title,
          content: d.content,
          sort_order: (i + 1) * 10,
        });
      }
      toast.success(`${defaults.length} Hilfetexte als Vorlagen gespeichert`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSeeding(false);
    }
  };

  if (!open) return null;

  const label = HELP_LABELS[context] || "Hilfe";

  return (
    <div
      className="fixed inset-0 z-[10050] flex"
      data-testid="help-slideover-root"
      onClick={close}
    >
      {/* Backdrop */}
      <div className="flex-1 bg-black/40 backdrop-blur-[2px]" />
      {/* Panel rechts */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:w-[420px] bg-card text-card-foreground border-l shadow-2xl flex flex-col animate-in slide-in-from-right duration-200"
        data-testid="help-slideover-panel"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <HelpCircle className="w-5 h-5 text-amber-600 shrink-0" />
            <div className="min-w-0">
              <h2 className="font-bold text-base leading-tight truncate">{label}</h2>
              <p className="text-[11px] text-muted-foreground">F1 zum Schließen · ESC zum Schließen</p>
            </div>
          </div>
          <button
            onClick={close}
            className="p-2 rounded-full hover:bg-muted text-muted-foreground"
            aria-label="Hilfe schliessen"
            data-testid="btn-help-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              Für diesen Bereich gibt es noch keine Hilfetexte.
            </p>
          ) : (
            items.map((it, idx) => (
              <div key={it.id || `default-${idx}`} className="border rounded-md bg-muted/20" data-testid={`help-item-${idx}`}>
                <div className="flex items-center gap-2 px-3 py-2 border-b bg-muted/30">
                  <BookOpen className="w-4 h-4 text-primary shrink-0" />
                  <span className="font-semibold text-sm">{it.title}</span>
                </div>
                <p className="px-3 py-2.5 text-sm whitespace-pre-line leading-relaxed">
                  {teileMitVerweisen(it.content).map((t, i) => t.ziel ? (
                    <button key={i} type="button" onClick={() => zumZiel(t.ziel)} data-testid="help-verweis" data-ziel={t.ziel}
                      className="inline-flex items-center gap-1 my-0.5 px-2 py-1 rounded-sm border border-primary/50 text-primary text-xs font-semibold hover:bg-primary/10 align-baseline">
                      {t.label}<ArrowRight className="w-3 h-3" />
                    </button>
                  ) : <span key={i}>{t.text}</span>)}
                </p>
              </div>
            ))
          )}
        </div>

        <div className="px-4 py-3 border-t shrink-0 space-y-2">
          {usingDefaults && items.length > 0 && (
            <button
              type="button"
              onClick={seedDefaults}
              disabled={seeding}
              data-testid="btn-help-seed"
              className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-sm bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Als Vorlage speichern (zum Anpassen)
            </button>
          )}
          <p className="text-[11px] text-muted-foreground text-center">
            Texte sind in Einstellungen → Textvorlagen editierbar
            <br />
            (doc_type: <code>{context}</code>, text_type: <code>hilfe</code>)
          </p>
        </div>
      </div>
    </div>
  );
};

export default HelpSlideOver;
